import { z } from 'zod';
import { bcp47, uuid } from './common.js';
import { costEstimate } from './credit.js';
import { listQueryInput, paginated } from './list.js';
import { difficultyLevel, documentKind, documentSchema, outlineJsonb, outlineStatus, versionOrigin, } from './document.js';
/**
 * Document action DTOs (§3.1). One schema per WS op, shared client + server.
 * AI ops return a `jobId` fast (§5.3/§6) — progress streams over `job:{jobId}`.
 */
/** Standard "job accepted" reply for every async (AI) op. */
export const jobAccepted = z.object({ jobId: uuid });
/**
 * Wizard knobs beyond the base fields below — carried as one `options` bag
 * (F-Estep step ③ sources / step ④ length / the illustrate flag) rather than
 * exploding the top-level DTO. `estimateActionCost` (@xdocs/core §21.2)
 * derives the `complexity_tier` from these signals server-side, never from a
 * user-supplied tier.
 */
export const generateOptions = z.object({
    /** F-Estep step ② topic/brief (also the F-Echat opening message). */
    brief: z.string().max(4000).optional(),
    /** F-Estep step ③ own-upload sources (F18); the public-domain source is `sourceBookId` below. */
    sourceFileIds: z.array(uuid).max(20).optional(),
    /** F-Estep step ④ target length (chapter count) — a tier-derivation signal, not a hard cap. */
    chapterCountTarget: z.number().int().positive().max(200).optional(),
    /** PRO illustrate stage (F1) on/off. */
    illustrate: z.boolean().default(false),
});
export const createDocumentInput = z.object({
    title: z.string().min(1, 'errors:document.title.required').max(300, 'errors:document.title.tooLong'),
    kind: documentKind,
    templateId: uuid.optional(),
    personaId: uuid.optional(),
    sourceBookId: uuid.optional(),
    difficultyLevel: difficultyLevel.default('basic'),
    language: bcp47.default('en'),
    /** F-Estep wizard knobs (brief, sources, length target, illustrate). */
    options: generateOptions.optional(),
});
/** F2 — expand a version without limit. */
export const expandDocumentInput = z.object({
    documentId: uuid,
    versionId: uuid,
    /** Optional free-text steer for the expansion. */
    instructions: z.string().max(2000).optional(),
});
/** F3 — cut then continue from a point. */
export const cutDocumentInput = z.object({
    documentId: uuid,
    versionId: uuid,
    /** Chapter id to cut at (inclusive) before continuing. */
    fromChapterId: z.string().min(1),
    instructions: z.string().max(2000).optional(),
});
/** F4 — elevate/change reading difficulty. */
export const difficultyDocumentInput = z.object({
    documentId: uuid,
    versionId: uuid,
    targetLevel: difficultyLevel,
});
/** F17 — translate into one or more target languages. */
export const translateDocumentInput = z.object({
    documentId: uuid,
    versionId: uuid,
    targetLanguages: z.array(bcp47).min(1, 'errors:document.translate.noLanguages').max(20),
});
// --- F2/F3/F4/F17 cost previews (paired with the `document.expand/cut/difficulty/
// translate` start ops above, same "estimate before start" split as
// `generate.estimate`/`generate.start`) -------------------------------------
//
// All four reuse the one `costEstimate` shape (`./credit.ts`) — "cost shown
// must equal what the worker actually debits" (F48 AC) holds by construction.
// `costEstimate.tierRequired` IS the `requirePro` signal (`./errors.ts`
// `tierRequiredAlert`): F2/F3/F4/F17 are PRO-only features end to end (every
// `action_credit_costs` row for these four `actionType`s seeds `tierRequired:
// 'pro'`, not just the `mastery` complexity tier), so a Free caller's
// estimate still resolves (never blocked at preview time — the chip must
// render the lock, per F48 AC "PRO-locked tier is a lock, not a dead
// control"), but the paired `document.expand`/`cut`/`difficulty`/`translate`
// start op MUST reject with `tierRequiredAlert('pro', actionType)` server-side
// when the caller isn't PRO — never a UI-only gate (§15).
/** F2 — expand cost preview. */
export const expandEstimateInput = z.object({ documentId: uuid, versionId: uuid });
export const expandEstimateResult = costEstimate; // actionType: 'expand'
/** F3 — cut-and-continue cost preview. */
export const cutEstimateInput = z.object({
    documentId: uuid,
    versionId: uuid,
    fromChapterId: z.string().min(1),
});
export const cutEstimateResult = costEstimate; // actionType: 'cut_continue'
/** F4 — difficulty-elevate cost preview; `units` scales with the level jump (AC: "cost scales with the level jump"). */
export const difficultyEstimateInput = z.object({
    documentId: uuid,
    versionId: uuid,
    targetLevel: difficultyLevel,
});
export const difficultyEstimateResult = costEstimate; // actionType: 'difficulty_elevate'
/** F17 — translate cost preview; `units` = `targetLanguages.length` (per-language cost, F48 AC). */
export const translateEstimateInput = z.object({
    documentId: uuid,
    versionId: uuid,
    targetLanguages: z.array(bcp47).min(1, 'errors:document.translate.noLanguages').max(20),
});
export const translateEstimateResult = costEstimate; // actionType: 'translate'
/** F15 — confirm (optionally edited) proposed outline; unblocks the draft-worker. */
export const outlineConfirmInput = z.object({
    documentId: uuid,
    outlineId: uuid,
    /** Present when the user edited the proposed structure before confirming. */
    edits: outlineJsonb.optional(),
});
/**
 * F15 — reject the proposed outline (stop; optionally note why). Distinct
 * from "Regenerate outline" (a client-side re-run that produces a fresh
 * proposal on the SAME outline row): reject flips `document_outlines.status`
 * to `'rejected'` and does NOT enqueue `draft-worker`.
 */
export const outlineRejectInput = z.object({
    documentId: uuid,
    outlineId: uuid,
    reason: z.string().max(500).optional(),
});
// --- F1/F-Estep wizard: generate.estimate / generate.start -------------------
/**
 * `generate.estimate` — cost preview from `action_credit_costs` (F48), shown
 * on the wizard's review step / before the F15 gate. Complexity is derived
 * from these signals server-side (§21.2), never asked of the user directly.
 */
export const generateEstimateInput = z.object({
    kind: documentKind,
    difficultyLevel: difficultyLevel.default('basic'),
    options: generateOptions.optional(),
});
/**
 * Reuses the one cost-estimate shape (`./credit.ts`) shared by every
 * chip/confirm-dialog/worker-debit call site (§21.2) — `actionType` is always
 * `'generate'` here.
 */
export const generateEstimateResult = costEstimate;
/**
 * `generate.start` — the "Generate · N cr" trigger (F1 step 1 review /
 * F-Estep ⑥). Takes an already-`document.create`d draft, debits the (near-0)
 * `outline` complexity tier (§21.3 debit-at-enqueue, idempotent by
 * `jobs.id`), and enqueues `outline-worker`. Drafting itself is charged
 * separately at `outline.confirm` — see F15.
 */
export const generateStartInput = z.object({ documentId: uuid });
// --- live pushes on document:{documentId} / outline-confirm:{documentId} ----
/**
 * Push on `document:{documentId}` (§5.5) — fired whenever a new version lands
 * (generate/expand/cut/difficulty/translate/restyle/edit). Intentionally
 * minimal, matching `jobProgressPush`'s (`./job.ts`) "signal, not payload"
 * convention — the client re-fetches via `document.get` on receipt.
 */
export const documentVersionPush = z.object({
    documentId: uuid,
    versionId: uuid,
    origin: versionOrigin,
});
/**
 * Push on `outline-confirm:{documentId}` (§5.5, F15) — the outline-worker's
 * proposal delivery AND the confirm/reject relay that unblocks (or stops)
 * the waiting `draft-worker`. `status` discriminates which edge fired.
 */
export const outlineConfirmPush = z.object({
    documentId: uuid,
    outlineId: uuid,
    status: outlineStatus,
});
// --- list / read -------------------------------------------------------------
export const documentSortColumns = ['createdAt', 'updatedAt', 'title', 'status'];
export const documentListQuery = listQueryInput(documentSortColumns, {
    defaultSort: 'updatedAt',
});
export const documentListResult = paginated(documentSchema);
export const getDocumentInput = z.object({ documentId: uuid });
